import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { TranslatePipe } from '../../filters/translate.pipe';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import ChatRestServices from '../../services/chat-rest.service';
import ChatService from '../../services/chat.service';
import RoutingService from '../../services/routing.service';
import ToolkitService from '../../services/toolkit.service';

@Component({
	standalone: true,
	selector: 'toolkit-client',
	templateUrl: './toolkit-client.component.html',
	imports: [CommonModule, TranslatePipe, LoaderOverlayComponent]
})
export default class ToolkitClientComponent implements OnInit {
	private routingService: RoutingService = inject(RoutingService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private route: ActivatedRoute = inject(ActivatedRoute);
	private toolkitService: ToolkitService = inject(ToolkitService);
	private chatRestServices: ChatRestServices = inject(ChatRestServices);
	private chatService: ChatService = inject(ChatService);

	vm: any = undefined;

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		this.routingService.validateAccess('toolkit').then(() => this.onResolve());
	}

	onResolve() {
		this.vm.loading = true;
		this.vm.error = false;
		this.vm.clientId = Number(this.route.snapshot.paramMap.get('id') || 0);

		this.vm.catalogue = null;
		this.vm.themes = [];
		this.vm.resources = [];
		this.vm.assessments = [];

		this.vm.client = null;
		this.vm.assignments = [];
		this.vm.assessmentAssignments = [];
		this.vm.stats = null;

		this.vm.back = this.back.bind(this);
		this.vm.themeName = this.themeName.bind(this);
		this.vm.resourceFormatLabel = this.resourceFormatLabel.bind(this);
		this.vm.difficultyLabel = this.difficultyLabel.bind(this);
		this.vm.statusPill = this.statusPill.bind(this);
		this.vm.openChat = this.openChat.bind(this);
		this.vm.assign = this.assign.bind(this);
		this.vm.openExerciseResult = this.openExerciseResult.bind(this);
		this.vm.openSurveyResult = this.openSurveyResult.bind(this);

		if (!this.vm.clientId) {
			this.vm.loading = false;
			this.vm.error = true;
			return;
		}

		this.toolkitService.getCatalogue({}, (catalogue) => {
			this.vm.catalogue = catalogue;
			this.vm.themes = Array.isArray(catalogue?.themes) ? catalogue.themes : [];
			this.vm.resources = Array.isArray(catalogue?.resources) ? catalogue.resources : [];
			this.vm.assessments = Array.isArray(catalogue?.assessments) ? catalogue.assessments : [];
			this.attachMeta();
		});

		this.toolkitService.getClientDetail(
			{ id: this.vm.clientId },
			(detail) => {
				this.vm.client = detail?.client || null;
				this.vm.assignments = Array.isArray(detail?.assignments) ? detail.assignments : [];
				this.vm.assessmentAssignments = Array.isArray(detail?.assessmentAssignments) ? detail.assessmentAssignments : [];
				this.attachMeta();
				this.vm.loading = false;
			},
			() => {
				this.vm.loading = false;
				this.vm.error = true;
			}
		);
	}

	openChat() {
		const friend = this.vm?.client || null;
		const id = friend?.id;
		if (!id) return;
		this.chatRestServices.getChatByFriendId({ friendUserId: id }, (chat) => {
			this.chatService.openChatById(friend, chat?.id);
		});
	}

	assign() {
		const clientId = this.vm?.clientId;
		if (!clientId) return;
		try {
			localStorage.setItem('toolkitAssignClientId', clientId.toString());
		} catch {}
		this.angularRoutingHelperService.setPath('/toolkit', false, false, { tab: 'library', assignClientId: clientId });
	}

	openExerciseResult(a: any) {
		const resId = a?._resource?.id || a?.resourceId;
		if (!resId) return;
		const clientId = this.vm?.clientId;
		const assignmentId = a?.id;
		this.angularRoutingHelperService.setPath(
			['/toolkit/preview', 'resource', resId],
			false,
			false,
			{ preview: 1, client_id: clientId, assignment_id: assignmentId, return_to: `/toolkit/client/${clientId}` }
		);
	}

	openSurveyResult(a: any) {
		const asmtId = a?._assessment?.id || a?.assessmentId;
		if (!asmtId) return;
		const clientId = this.vm?.clientId;
		const assignmentId = a?.id;
		this.angularRoutingHelperService.setPath(
			['/toolkit/assessment/preview', asmtId],
			false,
			false,
			{ preview: 1, client_id: clientId, assignment_id: assignmentId, return_to: `/toolkit/client/${clientId}` }
		);
	}

	private attachMeta() {
		if (!this.vm.resources?.length || !this.vm.client) return;
		const resources: any[] = this.vm.resources || [];
		const assessments: any[] = this.vm.assessments || [];

		(this.vm.assignments || []).forEach((a) => {
			a._resource = resources.find((r) => r?.id === a?.resourceId) || null;
		});
		(this.vm.assessmentAssignments || []).forEach((a) => {
			a._assessment = assessments.find((s) => s?.id === a?.assessmentId) || null;
		});

		this.computeStats();
	}

	private computeStats() {
		const tools: any[] = Array.isArray(this.vm.assignments) ? this.vm.assignments : [];
		const surveys: any[] = Array.isArray(this.vm.assessmentAssignments) ? this.vm.assessmentAssignments : [];

		const toolCompleted = tools.filter((a) => this.resourceIsCompleted(a)).length;
		const toolOverdue = tools.filter((a) => !this.resourceIsCompleted(a) && this.isOverdue(a?.dueAt));
		const overdueTitle = toolOverdue.length ? (toolOverdue[0]?._resource?.title || toolOverdue[0]?.resourceId || '') : '';

		const surveyPending = surveys.filter((a) => {
			const done = (a?.status || '').toString().toLowerCase() === 'completed' || a?.completedAt;
			return !done;
		});
		const pendingDue = surveyPending
			.filter((a) => !!a?.dueAt)
			.sort((a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime());
		const pendingFirst = pendingDue[0] || surveyPending[0] || null;
		const pendingLabel = pendingFirst
			? `${pendingFirst?._assessment?.title || pendingFirst?.assessmentId || ''}${pendingFirst?.dueAt ? ' (' + (pendingFirst.dueAt.toString().slice(0, 10) || '') + ')' : ''}`
			: '';

		this.vm.stats = {
			toolkitCompleted: toolCompleted,
			toolkitTotal: tools.length,
			overdueCount: toolOverdue.length,
			overdueTitle: overdueTitle,
			surveyPendingCount: surveyPending.length,
			surveyPendingLabel: pendingLabel
		};
	}

	back() {
		this.angularRoutingHelperService.setPath('/toolkit', false, false);
	}

	private isOverdue(dueAt: any): boolean {
		if (!dueAt) return false;
		const d = new Date(dueAt);
		if (!isFinite(d.getTime())) return false;
		const today = new Date();
		const t = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
		const due = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
		return due < t;
	}

	private resourceIsCompleted(a: any): boolean {
		const completions = Array.isArray(a?.completions) ? a.completions : [];
		return completions.length > 0;
	}

	statusPill(kind: 'toolkit' | 'survey', a: any): { key: string; cls: string } {
		if (kind === 'survey') {
			const status = (a?.status || '').toString().toLowerCase();
			if (status === 'completed' || a?.completedAt) {
				return { key: 'Toolkit.History.Status.Completed', cls: 'pill-done' };
			}
			if (this.isOverdue(a?.dueAt)) return { key: 'Toolkit.History.Status.Overdue', cls: 'pill-overdue' };
			return { key: 'Toolkit.History.Status.Pending', cls: 'pill-pending' };
		}

		if (this.resourceIsCompleted(a)) return { key: 'Toolkit.History.Status.Completed', cls: 'pill-done' };
		if (this.isOverdue(a?.dueAt)) return { key: 'Toolkit.History.Status.Overdue', cls: 'pill-overdue' };
		return { key: 'Toolkit.History.Status.Pending', cls: 'pill-pending' };
	}

	resourceFormatLabel(format: string): string {
		const f = (format || '').toString();
		if (!f) return '';
		if (f === 'psychoeducation') return 'Toolkit.Format.Psychoeducation';
		if (f === 'exercise') return 'Toolkit.Format.Exercise';
		if (f === 'worksheet') return 'Toolkit.Format.Worksheet';
		if (f === 'check-in' || f === 'checkin') return 'Toolkit.Format.CheckIn';
		if (f === 'planner') return 'Toolkit.Format.Planner';
		return f;
	}

	difficultyLabel(difficulty: string): string {
		const d = (difficulty || '').toString();
		if (!d) return '';
		if (d === 'easy') return 'Toolkit.Difficulty.Easy';
		if (d === 'medium') return 'Toolkit.Difficulty.Medium';
		if (d === 'hard') return 'Toolkit.Difficulty.Hard';
		return d;
	}

	themeName(themeId: any): string {
		const themes: any[] = Array.isArray(this.vm.themes) ? this.vm.themes : [];
		const found = themes.find((t) => (t?.id || '').toString() === (themeId || '').toString());

		const rawId = (found?.id ?? themeId ?? '').toString();
		const rawName = (found?.name ?? '').toString();

		const keyFromId = this.themeTranslationKey(rawId);
		if (keyFromId) return keyFromId;
		const keyFromName = this.themeTranslationKey(rawName);
		if (keyFromName) return keyFromName;

		return rawName || rawId || '';
	}

	private themeTranslationKey(input: string): string | null {
		const s = (input || '')
			.toString()
			.trim()
			.toLowerCase()
			.replace(/&/g, 'and')
			.replace(/[^\w\s]/g, ' ')
			.replace(/[_-]/g, ' ')
			.replace(/\s+/g, ' ');

		if (!s) return null;
		if (s === 'anxiety') return 'Toolkit.Theme.Anxiety';
		if (s === 'stress' || s === 'stress & overload' || s === 'stress and overload' || s === 'overload') return 'Toolkit.Theme.Stress';
		if (s === 'low mood' || s === 'lowmood') return 'Toolkit.Theme.LowMood';
		if (s === 'sleep') return 'Toolkit.Theme.Sleep';
		if (s === 'self esteem' || s === 'selfesteem' || s === 'self-esteem') return 'Toolkit.Theme.SelfEsteem';
		if (s === 'habits') return 'Toolkit.Theme.Habits';
		if (s === 'motivation') return 'Toolkit.Theme.Motivation';
		if (s === 'emotional regulation') return 'Toolkit.Theme.EmotionalRegulation';
		if (s === 'focus and concentration' || s === 'focus concentration') return 'Toolkit.Theme.FocusConcentration';
		if (s === 'relationships') return 'Toolkit.Theme.Relationships';
		return null;
	}
}
